import React, { useMemo, useState } from "react";
import { FileText, Save, Search, Trash2, X } from "lucide-react";
import {
  BILLABILITY_STATUSES,
  DATE_ANCHORS,
  INVOICE_STATUSES,
  PAYMENT_TERMS,
  billabilityStatusClass,
  billabilityStatusLabel,
  createEmptyInvoiceDraft,
  formatDate,
  formatMoney,
  formatQty,
  getConsumedQuantities,
  normalizedInvoiceStatusValue,
  parseDecimal,
  todayIso,
} from "./billingMath";

const draftFromInvoice = (invoice, lines = []) => {
  const unitPriceByLine = new Map(lines.map((line) => [String(line.id), Number(line.unit_price || 0)]));
  return {
    invoice_reference: invoice.invoice_reference || "",
    label: invoice.label || "",
    status: normalizedInvoiceStatusValue(invoice.status),
    billability_status: invoice.billability_status || "pending",
    issue_date_anchor: invoice.issue_date_anchor || "fixed",
    issue_date: invoice.issue_date || todayIso(),
    issue_project_id: invoice.issue_project_id ? String(invoice.issue_project_id) : "",
    issue_iteration_id: invoice.issue_iteration_id ? String(invoice.issue_iteration_id) : "",
    payment_terms_override: invoice.payment_terms_override || "",
    lines: (invoice.line_allocations || []).reduce((acc, allocation) => {
      const unitPrice = unitPriceByLine.get(String(allocation.line_id)) || 0;
      const fallbackQuantity =
        allocation.allocated_quantity ?? (unitPrice > 0 ? Number(allocation.allocated_amount_eur || 0) / unitPrice : "");
      acc[String(allocation.line_id)] = {
        mode: "quantity",
        value: String(fallbackQuantity),
      };
      return acc;
    }, {}),
  };
};

const isActiveProjectIteration = (iteration) => iteration?.project_status === "in_progress";

const hasProjectInvoice = (iteration) => Number(iteration?.project_invoice_count || 0) > 0;

const iterationSearchText = (iteration) =>
  [
    iteration?.label,
    iteration?.project_number,
    iteration?.project_name,
    iteration?.project_status,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const iterationOptionLabel = (iteration) => {
  const projectInvoiceCount = Number(iteration.project_invoice_count || 0);
  const iterationInvoiceCount = Number(iteration.iteration_invoice_count || 0);
  const suffixes = [];
  if (projectInvoiceCount > 0) {
    suffixes.push(`${projectInvoiceCount} facture${projectInvoiceCount > 1 ? "s" : ""} projet`);
  }
  if (iterationInvoiceCount > 0) {
    suffixes.push(`${iterationInvoiceCount} sur cette iteration`);
  }
  return `${iteration.label}${suffixes.length ? ` - ${suffixes.join(", ")}` : ""}`;
};

const projectOptionLabel = (project) => {
  const base = `${project.project_number ? `${project.project_number} - ` : ""}${project.name || `Projet ${project.id}`}`;
  const invoiceCount = Number(project.invoice_count || 0);
  return invoiceCount > 0
    ? `${base} - ${invoiceCount} facture${invoiceCount > 1 ? "s" : ""}`
    : base;
};

function IterationSelector({ options, value, onChange }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showMore, setShowMore] = useState(false);
  const selectedId = String(value || "");
  const selectedOption = options.find((option) => String(option.id) === selectedId);
  const query = searchTerm.trim().toLowerCase();
  const filteredOptions = query
    ? options.filter((option) => iterationSearchText(option).includes(query))
    : options;

  const activeWithoutInvoice = filteredOptions.filter(
    (option) => isActiveProjectIteration(option) && !hasProjectInvoice(option),
  );
  const activeWithInvoice = filteredOptions.filter(
    (option) => isActiveProjectIteration(option) && hasProjectInvoice(option),
  );
  const hiddenOtherOptions = options.filter((option) => !isActiveProjectIteration(option));
  const hiddenOtherProjectCount = new Set(hiddenOtherOptions.map((option) => option.project_id || option.id)).size;
  const otherOptions = query || showMore
    ? filteredOptions.filter((option) => !isActiveProjectIteration(option))
    : [];

  const visibleIds = new Set(
    [...activeWithoutInvoice, ...activeWithInvoice, ...otherOptions].map((option) => String(option.id)),
  );
  const currentSelection = selectedOption && !visibleIds.has(selectedId) ? [selectedOption] : [];
  const visibleCount =
    activeWithoutInvoice.length + activeWithInvoice.length + otherOptions.length + currentSelection.length;

  const renderGroup = (label, items) =>
    items.length ? (
      <optgroup key={label} label={label}>
        {items.map((iteration) => (
          <option key={iteration.id} value={iteration.id}>
            {iterationOptionLabel(iteration)}
          </option>
        ))}
      </optgroup>
    ) : null;

  const selectedHint = selectedOption
    ? isActiveProjectIteration(selectedOption)
      ? hasProjectInvoice(selectedOption)
        ? "Projet actif avec facture"
        : "Projet actif sans facture client"
      : "Projet hors actifs"
    : "Les projets actifs sans facture client sont proposes en premier.";

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="search"
          className="w-full rounded-lg border border-slate-200 py-2 pl-8 pr-3 text-sm"
          placeholder="Chercher projet, numero ou iteration"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
      </div>
      <select
        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        value={value}
        onChange={onChange}
      >
        <option value="">Choisir une iteration</option>
        {renderGroup("Selection actuelle", currentSelection)}
        {renderGroup("Projets actifs sans facture client", activeWithoutInvoice)}
        {renderGroup("Projets actifs avec facture", activeWithInvoice)}
        {renderGroup("Autres projets", otherOptions)}
      </select>
      <div className="flex min-h-[1.5rem] flex-wrap items-center justify-between gap-2">
        {hiddenOtherProjectCount > 0 && !query ? (
          <button
            type="button"
            onClick={() => setShowMore((value) => !value)}
            className="text-xs font-medium text-emerald-700 hover:text-emerald-800"
          >
            {showMore ? "Masquer" : `Voir plus (${hiddenOtherProjectCount})`}
          </button>
        ) : (
          <span />
        )}
        <span className="text-xs text-slate-500">
          {query ? `${visibleCount} resultat${visibleCount > 1 ? "s" : ""}` : selectedHint}
        </span>
      </div>
      {!options.length && (
        <p className="text-xs text-slate-500">Aucune iteration trouvee pour ce client.</p>
      )}
    </div>
  );
}

export default function InvoiceEditor({
  mode = "create",
  invoice = null,
  lines,
  cashEvents,
  projectOptions = [],
  iterationOptions = [],
  clientPaymentTerms,
  onCreate,
  onUpdate,
  onUpdateBillability,
  onDelete,
  onCancel,
  canEditBillability = false,
  showError,
}) {
  const isEdit = mode === "edit";
  const [draft, setDraft] = useState(() => (isEdit && invoice ? draftFromInvoice(invoice, lines) : createEmptyInvoiceDraft()));
  const [saving, setSaving] = useState(false);
  const [billabilitySaving, setBillabilitySaving] = useState(false);

  const consumedByLine = useMemo(
    () => getConsumedQuantities(cashEvents, lines, isEdit && invoice ? invoice.id : null),
    [cashEvents, lines, isEdit, invoice],
  );

  const draftLines = useMemo(() => {
    return lines.map((line) => {
      const consumed = consumedByLine.get(String(line.id)) || 0;
      const remaining = Math.max(Number(line.quantity || 0) - consumed, 0);
      const lineDraft = draft.lines[String(line.id)] || { mode: "quantity", value: "" };
      const value = parseDecimal(lineDraft.value);
      const quantity =
        !Number.isFinite(value) || value <= 0
          ? 0
          : lineDraft.mode === "percent"
            ? (Number(line.quantity || 0) * Math.min(value, 100)) / 100
            : value;
      const cappedQuantity = Math.min(quantity, remaining);
      return {
        line,
        consumed,
        remaining,
        draft: lineDraft,
        selectedQuantity: cappedQuantity,
        amount: Math.round(Number(line.unit_price || 0) * cappedQuantity * 100) / 100,
      };
    });
  }, [lines, consumedByLine, draft.lines]);

  const selectedLines = draftLines.filter((item) => item.selectedQuantity > 0);
  const total = selectedLines.reduce((sum, item) => sum + item.amount, 0);

  const selectedIteration = iterationOptions.find(
    (option) => String(option.id) === String(draft.issue_iteration_id),
  );
  const availableIterationOptions = draft.issue_project_id
    ? iterationOptions.filter(
        (option) => String(option.project_id) === String(draft.issue_project_id),
      )
    : iterationOptions;
  const billabilityBy = invoice?.billability_decided_by_name || invoice?.billability_decided_by_email || "";
  const resolvedIssueDate =
    draft.issue_date_anchor === "fixed"
      ? draft.issue_date
      : selectedIteration
        ? draft.issue_date_anchor === "iteration_start"
          ? selectedIteration.start_date
          : selectedIteration.end_date
        : null;

  const updateLineDraft = (lineId, patch) => {
    setDraft((prev) => ({
      ...prev,
      lines: {
        ...prev.lines,
        [String(lineId)]: {
          mode: "quantity",
          value: "",
          ...(prev.lines[String(lineId)] || {}),
          ...patch,
        },
      },
    }));
  };

  const fillAllRemaining = () => {
    const nextLines = {};
    draftLines.forEach(({ line, remaining }) => {
      if (remaining > 0) nextLines[String(line.id)] = { mode: "quantity", value: String(remaining) };
    });
    setDraft((prev) => ({ ...prev, lines: nextLines }));
  };

  const clearLines = () => setDraft((prev) => ({ ...prev, lines: {} }));

  const handleProjectChange = (value) => {
    setDraft((prev) => {
      const selectedDraftIteration = iterationOptions.find(
        (option) => String(option.id) === String(prev.issue_iteration_id),
      );
      const iterationMatchesProject =
        selectedDraftIteration && String(selectedDraftIteration.project_id) === String(value);
      return {
        ...prev,
        issue_project_id: value,
        issue_iteration_id: iterationMatchesProject ? prev.issue_iteration_id : "",
      };
    });
  };

  const handleIterationChange = (value) => {
    const iteration = iterationOptions.find((option) => String(option.id) === String(value));
    setDraft((prev) => ({
      ...prev,
      issue_iteration_id: value,
      issue_project_id: iteration?.project_id ? String(iteration.project_id) : prev.issue_project_id,
    }));
  };

  const toggleLine = (lineId, remaining, selectedQuantity) => {
    setDraft((prev) => {
      const nextLines = { ...prev.lines };
      const key = String(lineId);
      if (selectedQuantity > 0 || remaining <= 0) {
        delete nextLines[key];
      } else {
        nextLines[key] = { mode: "quantity", value: String(remaining) };
      }
      return { ...prev, lines: nextLines };
    });
  };

  const buildAllocations = () =>
    selectedLines.map(({ line, draft: lineDraft, selectedQuantity }) => {
      if (lineDraft.mode === "percent") {
        return { line_id: line.id, percent: Math.min(parseDecimal(lineDraft.value), 100) };
      }
      return { line_id: line.id, quantity: selectedQuantity };
    });

  const handleBillabilityChange = async (event) => {
    const value = event.target.value;
    setDraft((prev) => ({ ...prev, billability_status: value }));
    if (!isEdit || !invoice || !onUpdateBillability) return;
    setBillabilitySaving(true);
    try {
      const ok = await onUpdateBillability(invoice, value);
      if (!ok) {
        setDraft((prev) => ({ ...prev, billability_status: invoice.billability_status || "pending" }));
      }
    } finally {
      setBillabilitySaving(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;
    if (!selectedLines.length && !isEdit) {
      showError("Selectionnez au moins une quantite a facturer");
      return;
    }
    if (draft.issue_date_anchor !== "fixed" && !draft.issue_iteration_id) {
      showError("Choisissez une iteration pour la date de facture");
      return;
    }
    if (draft.issue_date_anchor === "fixed" && !draft.issue_date) {
      showError("Choisissez une date fixe de facture");
      return;
    }
    const payload = {
      label: draft.label.trim() || draft.invoice_reference.trim() || null,
      status: draft.status,
      invoice_reference: draft.invoice_reference.trim() || null,
      issue_date_anchor: draft.issue_date_anchor,
      issue_project_id: draft.issue_project_id ? Number(draft.issue_project_id) : null,
      issue_iteration_id: draft.issue_date_anchor === "fixed" ? null : Number(draft.issue_iteration_id),
      issue_date: draft.issue_date_anchor === "fixed" ? draft.issue_date : null,
      payment_terms_override: draft.payment_terms_override.trim() || null,
    };
    if (selectedLines.length) {
      payload.line_allocations = buildAllocations();
    }
    setSaving(true);
    try {
      const ok = isEdit ? await onUpdate(invoice, payload) : await onCreate(payload);
      if (ok && !isEdit) setDraft(createEmptyInvoiceDraft());
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-3 rounded-xl border border-emerald-200 bg-emerald-50/40 p-3 sm:p-4"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
          <FileText className="h-4 w-4" />
          {isEdit ? "Éditer la facture" : "Nouvelle facture"}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-600">
            Total&nbsp;: <span className="font-semibold text-slate-950">{formatMoney(total)}</span>
          </span>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg p-1 text-slate-400 hover:bg-white hover:text-slate-600"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <input
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
          placeholder="Reference facture"
          value={draft.invoice_reference}
          onChange={(event) => setDraft({ ...draft, invoice_reference: event.target.value })}
        />
        <input
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
          placeholder="Libelle"
          value={draft.label}
          onChange={(event) => setDraft({ ...draft, label: event.target.value })}
        />
        <select
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
          value={normalizedInvoiceStatusValue(draft.status)}
          onChange={(event) => setDraft({ ...draft, status: event.target.value })}
        >
          {INVOICE_STATUSES.map((status) => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </select>
        <select
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
          value={draft.payment_terms_override}
          onChange={(event) => setDraft({ ...draft, payment_terms_override: event.target.value })}
          title="Conditions de règlement (sert au calcul des encaissements)"
        >
          <option value="">
            {clientPaymentTerms ? `Conditions du client (${clientPaymentTerms})` : "Conditions du client"}
          </option>
          {PAYMENT_TERMS.map((term) => (
            <option key={term} value={term}>
              {term}
            </option>
          ))}
        </select>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <label className="text-xs font-medium text-slate-500">
          Projet (facultatif)
          <select
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900"
            value={draft.issue_project_id}
            onChange={(event) => handleProjectChange(event.target.value)}
          >
            <option value="">Sans projet</option>
            {projectOptions.map((project) => (
              <option key={project.id} value={project.id}>
                {projectOptionLabel(project)}
              </option>
            ))}
          </select>
        </label>
        <select
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
          value={draft.issue_date_anchor}
          onChange={(event) => setDraft({ ...draft, issue_date_anchor: event.target.value })}
        >
          {DATE_ANCHORS.map((anchor) => (
            <option key={anchor.value} value={anchor.value}>
              {anchor.label}
            </option>
          ))}
        </select>
        {draft.issue_date_anchor === "fixed" ? (
          <input
            type="date"
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            value={draft.issue_date}
            onChange={(event) => setDraft({ ...draft, issue_date: event.target.value })}
          />
        ) : (
          <div className="sm:col-span-1">
            <IterationSelector
              options={availableIterationOptions}
              value={draft.issue_iteration_id}
              onChange={(event) => handleIterationChange(event.target.value)}
            />
            {draft.issue_iteration_id && (
              <p className="mt-1 text-xs text-slate-500">
                {resolvedIssueDate ? (
                  <>
                    Émission le <span className="font-semibold text-slate-700">{formatDate(resolvedIssueDate)}</span>
                    {" "}({draft.issue_date_anchor === "iteration_start" ? "début" : "fin"} d'itération)
                  </>
                ) : (
                  <span className="text-amber-600">Dates de l'itération non renseignées</span>
                )}
              </p>
            )}
          </div>
        )}
        <div className="flex items-center justify-end gap-2 sm:col-span-2 lg:col-span-1">
          <button
            type="button"
            onClick={fillAllRemaining}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 hover:border-slate-300"
          >
            Tout le reste
          </button>
          <button
            type="button"
            onClick={clearLines}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 hover:border-slate-300"
          >
            Vider
          </button>
        </div>
      </div>

      {isEdit && (
        <div className="grid gap-2 rounded-lg border border-slate-100 bg-white px-3 py-2 sm:grid-cols-[minmax(0,1fr)_220px] sm:items-center">
          <div className="min-w-0">
            <div className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium ${billabilityStatusClass(draft.billability_status)}`}>
              {billabilityStatusLabel(draft.billability_status)}
            </div>
            {billabilityBy && (
              <div className="mt-1 truncate text-xs text-slate-500">
                Valide par {billabilityBy}
              </div>
            )}
          </div>
          <label className="text-xs font-medium text-slate-500">
            Facturabilité
            <select
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 disabled:bg-slate-50 disabled:text-slate-400"
              value={draft.billability_status || "pending"}
              onChange={handleBillabilityChange}
              disabled={!canEditBillability || billabilitySaving}
              title={canEditBillability ? "Modifier la facturabilité" : "Domaine « Facturabilité » requis pour cette itération"}
            >
              {BILLABILITY_STATUSES.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {draftLines.map(({ line, remaining, selectedQuantity, amount }) => {
          const selected = selectedQuantity > 0;
          return (
            <button
              key={`chip-${line.id}`}
              type="button"
              disabled={remaining <= 0 && !selected}
              onClick={() => toggleLine(line.id, remaining, selectedQuantity)}
              className={`rounded-full border px-3 py-1.5 text-xs ${
                selected
                  ? "border-emerald-400 bg-emerald-50 text-emerald-800"
                  : remaining <= 0
                    ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
                    : "border-slate-200 bg-white text-slate-700 hover:border-emerald-300"
              }`}
            >
              <span className="font-medium">{line.name}</span>
              <span className="ml-2 text-slate-500">
                {selected ? formatMoney(amount) : `${formatQty(remaining)} dispo`}
              </span>
            </button>
          );
        })}
        {!draftLines.length && <span className="text-xs text-slate-500">Aucune ligne de commande.</span>}
      </div>

      {selectedLines.length > 0 && (
        <div className="grid gap-2 lg:grid-cols-2">
          {selectedLines.map(({ line, remaining, draft: lineDraft, amount }) => (
            <div
              key={`row-${line.id}`}
              className="grid grid-cols-[1fr_72px_88px_92px] items-center gap-2 rounded-lg border border-slate-100 bg-white px-3 py-2 text-sm"
            >
              <div className="min-w-0">
                <div className="truncate font-medium">{line.name}</div>
                <div className="text-xs text-slate-500">{formatQty(remaining)} disponible</div>
              </div>
              <select
                className="rounded-lg border border-slate-200 px-2 py-1 text-xs"
                value={lineDraft.mode}
                onChange={(event) => updateLineDraft(line.id, { mode: event.target.value })}
              >
                <option value="quantity">Qté</option>
                <option value="percent">%</option>
              </select>
              <input
                className="rounded-lg border border-slate-200 px-2 py-1 text-right text-sm"
                value={lineDraft.value}
                onChange={(event) => updateLineDraft(line.id, { value: event.target.value })}
                placeholder={lineDraft.mode === "percent" ? "% ligne" : "Qt"}
              />
              <div className="text-right font-semibold">{formatMoney(amount)}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        {isEdit && onDelete ? (
          <button
            type="button"
            onClick={() => onDelete(invoice)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50"
          >
            <Trash2 className="h-4 w-4" />
            Supprimer
          </button>
        ) : (
          <span />
        )}
        <button
          type="submit"
          disabled={saving || (!selectedLines.length && !isEdit)}
          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white ${
            saving || (!selectedLines.length && !isEdit) ? "bg-slate-300" : "bg-emerald-600 hover:bg-emerald-700"
          }`}
        >
          {isEdit ? <Save className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
          {isEdit ? "Enregistrer" : "Creer facture"}
        </button>
      </div>
    </form>
  );
}
